import React, { useEffect, useMemo, useRef, useState } from "react";
import { TailSpin } from "react-loader-spinner";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import { Container, Divider, IconButton, Typography } from "@mui/material";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import { useGetActiveOrganizationsQuery, useGetInProgressOrganizationsQuery, useGetOrgByIdQuery } from "state/rtkQuery/org/orgQueries";
import { useGetPlatformsQuery } from "state/rtkQuery/platform/platformQueries";
import { useUpdateUserMembershipBulkMutation, useUpdateUserOrgStatusMutation } from "state/rtkQuery/user/userMutations";
import { useGetCostCenterUsersQuery, useGetUsersByOrgAndStatusQuery, useLazyGetUserQuery } from "state/rtkQuery/user/userQueries";

import AdminDataGrid from "./components/AdminDataGrid/AdminDataGrid";
import AdminUserActions from "./components/AdminUserActions/AdminUserActions";
import MultiSelectModal from "./components/MultiSelectModal/MultiSelectModal";
import ReasonColumn from "./components/ReasonColumn/ReasonColumn";
import UserDetailsModal from "./components/UserDetailsModal/UserDetailsModal";

import { useUserCapabilities } from "components/RequirePermission/useCapabilities";

import { getMSALToken } from "../../utils/getMSALToken";
import { FilterChips, FilterPanel } from "components/shared/FilterPanel/FilterPanel";
import { useFilterPanel } from "components/shared/FilterPanel/useFilterPanel";

import "./AdminDashboard.css";

const AdminDashboard = ({ tabsSlot, headerActions, refreshTick = 0, ...props }) => {
  const { instance, accounts } = useMsal();
  const { orgId } = useParams();

  const [authToken, setAuthToken] = useState();

  const userEmail = useSelector((state) => state.auth?.userAccount?.username);

  // Approve/Reject (single row + bulk) requires the capability to update a
  // user's org membership status — this page had no gating at all before,
  // so this is a brand-new check, same capability as JoinedTab's Actions
  // column and UlbRequestsTable's usage bundle pattern. useUserCapabilities
  // is already scoped to THIS org's role (see useEffectiveOrgPermission): it
  // checks the specific role useEffectiveOrgRole(orgId) reports for this org
  // against THAT role's own permissions, not the user's global cross-org
  // union — this is the exact bug class that was found live: an
  // OrganizationOwner-on-a-different-org, Leadership-on-this-org user was
  // incorrectly seeing Actions here, because Leadership's role definition
  // does NOT include user:id:update but the old global-union check couldn't
  // tell the two apart.
  const { canUpdateUser } = useUserCapabilities(orgId);

  // Loader States
  const [isPageLoading, setIsPageLoading] = useState(false);

  // DataGrid data states
  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);
  const [usersCostCenters, setUsersCostCenters] = useState({});

  // Multi-select data states
  const [showModalMulti, setShowModalMulti] = useState(false);
  const [multiSelectAction, setMultiSelectAction] = useState();
  const [rejectReason, setRejectReason] = useState("");
  const [otherReason, setOtherReason] = useState("");
  const [selectedRows, setSelectedRows] = useState([]);
  const [isMultiActionLoading, setIsMultiActionLoading] = useState(false);
  const [showUserDetailsModal, setShowUserDetailsModal] = useState(false);
  const [selectedUserDetails, setSelectedUserDetails] = useState(null);
  const [isUserDetailsLoading, setIsUserDetailsLoading] = useState(false);
  const [userDetailsError, setUserDetailsError] = useState("");

  // Get MSAL token
  useEffect(() => {
    getMSALToken(instance, accounts, setAuthToken);
  }, []);
  // RTK Query to get platform data
  const {
    data: platformData,
    isLoading: isPlatformDataLoading,
    isSuccess: isPlatformDataSuccess,
  } = useGetPlatformsQuery();

  const { data: allOrgs, isSuccess: isOrgsSuccess } =
    useGetActiveOrganizationsQuery();
  const { data: inProgressOrgs, isSuccess: isInProgressOrgsSuccess } =
    useGetInProgressOrganizationsQuery();
  const { data: costCentersData, isSuccess: isCostCentersSuccess } =
    useGetCostCenterUsersQuery();

  const [triggerGetUser] = useLazyGetUserQuery();

  // RTK Query to get org data by orgId
  const { data: orgData, isLoading: isOrgDataLoading } =
    useGetOrgByIdQuery(orgId);
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(10); // Set your default here

  // The filters offered here — all switched-on filters apply together (the API ANDs them).
  const filterFields = useMemo(
    () => [
      { key: "email", label: "Email", type: "text" },
      { key: "first_name", label: "First Name", type: "text" },
      { key: "last_name", label: "Last Name", type: "text" },
      { key: "changed_date", label: "Request Date", type: "date" },
    ],
    []
  );
  const { filters, setFilters, query: filterQuery } = useFilterPanel(filterFields, () => setCurrentPage(1));

  // RTK Query to get users by org and status
  const {
    data: orgRequestsData,
    isLoading: isOrgRequestsLoading,
    isFetching: isFetchingOrgRequestsLoading,
    isSuccess: isOrgRequestsSuccess,
    isError: isOrgRequestError,
    refetch: refetchOrgRequests,
  } = useGetUsersByOrgAndStatusQuery(
    {
      orgId: orgId,
      status: "Pending",
      platformId: platformData?._id,
      page: currentPage,
      perPage: recordsPerPage,
      extraFilters: filterQuery,
    },
    { skip: !isPlatformDataSuccess, refetchOnMountOrArgChange: true }
  );

  // The page's Refresh button bumps refreshTick; reload the list when it changes.
  const lastRefreshTick = useRef(refreshTick);
  useEffect(() => {
    if (refreshTick === lastRefreshTick.current) return;
    lastRefreshTick.current = refreshTick;
    refetchOrgRequests();
  }, [refreshTick]); // eslint-disable-line

  // Set rows and usersCostCenters state based on orgRequestsData
  useEffect(() => {
    if (isOrgRequestsSuccess) {
      setRows(orgRequestsData?.users || []);
    } else if (isOrgRequestError) {
      setRows([]);
    } else if (isFetchingOrgRequestsLoading) {
      setRows([]); // Clear rows while loading new page
    }
  }, [isOrgRequestsSuccess, isOrgRequestError, isFetchingOrgRequestsLoading, orgRequestsData]);

  // Active + In-Progress are the only two org-level statuses this system
  // has — a joined membership can be recorded against an org in either
  // status, so both lists are merged here or the User Profile modal's
  // Organization Memberships table falls back to showing a raw org ID for
  // any org that isn't currently Active.
  const orgIdToName = useMemo(() => {
    const orgs = [
      ...(isOrgsSuccess && Array.isArray(allOrgs) ? allOrgs : []),
      ...(isInProgressOrgsSuccess && Array.isArray(inProgressOrgs) ? inProgressOrgs : []),
    ];
    return orgs.reduce((acc, org) => {
      acc[org._id] = org.name;
      return acc;
    }, {});
  }, [allOrgs, isOrgsSuccess, inProgressOrgs, isInProgressOrgsSuccess]);

  const costCenterById = useMemo(() => {
    const costCenters = costCentersData?.cost_centers;
    if (!isCostCentersSuccess || !Array.isArray(costCenters)) {
      return {};
    }

    return costCenters.reduce((acc, cc) => {
      acc[cc._id] = cc.name;
      return acc;
    }, {});
  }, [costCentersData, isCostCentersSuccess]);

  const handleOpenUserDetails = async (row) => {
    setShowUserDetailsModal(true);
    setIsUserDetailsLoading(true);
    setUserDetailsError("");
    try {
      const result = await triggerGetUser({
        email: row.email,
        platformId: platformData?._id,
      });
      const users = Array.isArray(result?.data) ? result.data : [];
      const exactMatch = users.find((user) => user._id === row.id);
      setSelectedUserDetails(exactMatch || users[0] || null);

      if (!users.length) {
        setUserDetailsError("Could not find user details for this request.");
      }
    } catch (error) {
      setSelectedUserDetails(null);
      setUserDetailsError("Failed to load user details. Please try again.");
    } finally {
      setIsUserDetailsLoading(false);
    }
  };

  // Set columns for MUI datagrid
  useEffect(() => {
    // if (rows && Object.keys(usersCostCenters).length > 0) {
    if (rows) {
      setColumns([
        // Requester's Name
        {
          field: "name",
          headerName: "Requester Name",
          headerClassName: "orgName-column-header",
          headerAlign: "left",
          align: "left",
          flex: 1.3,
          minWidth: 200,
          renderCell: (params) => (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
              <Box sx={{ width: 16, display: "flex", justifyContent: "center", flexShrink: 0 }}>
                {params.row.isNewUser && (
                  <Tooltip sx={{fontSize:"14px"}} title="New User: Approving this request will consume 1 paid license.">
                    <PaidOutlinedIcon sx={{ color: "#00ab06", fontSize: 16 }} />
                  </Tooltip>
                )}
              </Box>
              <Tooltip title={params.row.name}>
                <span className="table-cell-trucate">{params.row.name}</span>
              </Tooltip>
            </Box>
          ),
        },
        // Requester's Email
        {
          field: "email",
          headerName: "Requester Email",
          description: "Requester Email",
          flex: 1.4,
          minWidth: 200,
          headerAlign: "left",
          align: "left",
          filterable: false,
          renderCell: (params) => (
            <Tooltip title={params.row.email}>
              <span
                className="table-cell-trucate-email"
                style={{ paddingLeft: "None" }}
              >
                {params.row.email}
              </span>
            </Tooltip>
          ),
        },
        // Reason for Request
        {
          field: "reason",
          headerName: "Reason for Request",
          headerClassName: "",
          headerAlign: "center",
          align: "center",
          flex: 4,
          minWidth: 320,
          renderCell: (params) => <ReasonColumn params={params} />,
        },
        // Cost Center field (commented out for now)
        // {
        //   field: "costCenter",
        //   headerName: "Cost Center",
        //   headerAlign: "center",
        //   align: "center",
        //   width: 190,
        //   renderCell: (params) => (
        //     <TextField
        //       sx={{
        //         "& .MuiInputBase-input": { padding: "7.5px 14px" },
        //       }}
        //       id="costCenter"
        //       select
        //       value={usersCostCenters[params.row.id]}
        //       onChange={(e) =>
        //         setUsersCostCenters((prev) => ({
        //           ...prev,
        //           [params.row.id]: e.target.value,
        //         }))
        //       }
        //       variant="outlined"
        //       style={{ width: "100%" }}
        //     >
        //       {costCenters.map((option) => (
        //         <MenuItem key={option.id} value={option.name}>
        //           {option.name}
        //         </MenuItem>
        //       ))}
        //     </TextField>
        //   ),
        // },
        // Request Date
        {
          field: "date",
          headerName: "Request Date",
          flex: 0.9,
          minWidth: 120,
          align: "center",
          headerAlign: "center",
          filterable: true,
          valueGetter: (params) => {
            // Return the date as a Date object for proper sorting
            return new Date(params.row.date);
          },
          renderCell: (params) => (
            <span style={{ fontSize: "12px" }}>{params.row.date}</span>
          ),
        },
        // Actions Buttons
        {
          field: "profile",
          headerName: "Profile",
          flex: 0.5,
          minWidth: 70,
          align: "center",
          headerAlign: "center",
          sortable: false,
          filterable: false,
          renderCell: (params) => (
            <Tooltip title="View user details">
              <IconButton
                size="small"
                aria-label="View user details"
                onClick={() => handleOpenUserDetails(params.row)}
                sx={{ p: 0.25, color: "#0b756c" }}
              >
                <InfoOutlinedIcon sx={{ color: "#0084ff", fontSize: 22 }} />
              </IconButton>
            </Tooltip>
          ),
        },
        // Actions Buttons
        canUpdateUser && {
          field: "action",
          headerAlign: "center",
          headerName: "Actions",
          align: "center",
          flex: 0.8,
          minWidth: 110,
          filterable: false,
          renderCell: (params) => (
            <AdminUserActions
              {...{ params }}
              platformId={platformData?._id}
              orgData={orgData}
              userEmail={userEmail}
              onRequestActionHandler={handleMultiSelectAction}
              isLoading={isMultiActionLoading}
              canUpdateUser={canUpdateUser}
            />
          ),
        },
      ].filter(Boolean));
    }
  }, [rows, platformData?._id, orgData, userEmail, isMultiActionLoading, canUpdateUser]);

  useEffect(() => {
    // When a multi-action is in progress and the table refetch is done, stop loading
    if (isMultiActionLoading && !isFetchingOrgRequestsLoading) {
      setIsMultiActionLoading(false);
      setSelectedRows([]);
    }
  }, [isMultiActionLoading, isFetchingOrgRequestsLoading]);

  // RTK Query for bulk user org status (accept/reject requests) membership updates
  const [updateUserMembershipBulk, { isLoading: isUpdateUserMembershipBulkLoading }] =
    useUpdateUserMembershipBulkMutation();

  // RTK Query to update user org status (accept/reject requests) for single user
  const [updateUserOrgStatus, { isLoading: isUpdateUserOrgStatusLoading }] =
    useUpdateUserOrgStatusMutation();

  const handleMultiSelectAction = async (
    action,
    userIds = selectedRows,
    rejectReasonArg = rejectReason,
    otherReasonArg = otherReason
  ) => {
    setIsMultiActionLoading(true);
    handleClose();
    try {
      // Ensure we have an array of user IDs
      const ids = Array.isArray(userIds) ? userIds : [userIds];

      // If only one user is selected, use the single-user mutation so failures are reported correctly
      if (ids.length === 1) {
        const singlePayload = {
          platforms: {
            [platformData._id]: {
              organizations: {
                [orgData._id]: {
                  membership_status: action === "rejected" ? "Rejected" : "Requested",
                  reason:
                    action === "rejected"
                      ? rejectReasonArg === "Other"
                        ? otherReasonArg
                        : rejectReasonArg
                      : `Approved by admin ${userEmail} from ECR Portal`,
                  changed_by: userEmail.toLowerCase(),
                },
              },
            },
          },
        };

        await updateUserOrgStatus({
          userId: ids[0],
          body: singlePayload,
          attribute: "membership_status",
        }).unwrap();

        setIsMultiActionLoading(false);
        setSelectedRows([]);
        return;
      }

      // Build bulk payload for multiple selected users
      const bulkPayload = {
        platforms: {
          [platformData._id]: {
            organizations: {
              [orgData._id]: {
                membership_status: action === "rejected" ? "Rejected" : "Requested",
                reason:
                  action === "rejected"
                    ? rejectReasonArg === "Other"
                      ? otherReasonArg
                      : rejectReasonArg
                    : `Approved by admin ${userEmail} from ECR Portal`,
                changed_by: userEmail.toLowerCase(),
              },
            },
          },
        },
        user_ids: ids, // Array of selected user IDs
      };

      // Single API call for all selected users
      await updateUserMembershipBulk({
        body: bulkPayload,
        invalidateStatuses: ["Pending"],
      }).unwrap();

      // Clear selections and loading with minimum 1 second wait time
      setTimeout(() => {
        setIsMultiActionLoading(false);
        setSelectedRows([]);
      }, 1000);
    } catch (error) {
      setIsMultiActionLoading(false);
      console.error("Error updating users:", error);
    }
  };

  // Close MultiSelectModal
  const handleClose = (event, reason) => {
    if (reason === "backdropClick") {
    } else {
      setShowModalMulti(false);
      setRejectReason("");
      setOtherReason("");
    }
  };

  return (
    <Container
      sx={{ maxWidth: "1700px !important", flex: "1", marginTop: "3rem" }}
    >
      {/* If page is loading or platform/org data is loading, show loader */}
      {isPageLoading || isPlatformDataLoading || isOrgDataLoading ? (
        <Box
          sx={{
            height: 420,
            marginTop: "4rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "auto",
          }}
        >
          <TailSpin
            color="hsl(81.43deg 69.37% 43.53%)"
            height={50}
            width={50}
          />
        </Box>
      ) : (
        <div>
          <UserDetailsModal
            open={showUserDetailsModal}
            onClose={() => {
              setShowUserDetailsModal(false);
              setSelectedUserDetails(null);
              setUserDetailsError("");
            }}
            user={selectedUserDetails}
            platformId={platformData?._id}
            orgIdToName={orgIdToName}
            costCenterById={costCenterById}
            isLoading={isUserDetailsLoading}
            error={userDetailsError}
          />
          {/* Show Multi-Select confirmation modal */}
          {showModalMulti && (
            <MultiSelectModal
              show={showModalMulti}
              close={handleClose}
              selectedRows={selectedRows}
              allRows={rows}
              onRequestActionHandler={handleMultiSelectAction}
              approvalAction={multiSelectAction}
              rejectReason={rejectReason}
              setRejectReason={setRejectReason}
              otherReason={otherReason}
              setOtherReason={setOtherReason}
            />
          )}
          {/* Top */}
          <Box sx={{ height: "calc(100vh - 240px)", minHeight: 600 }}>
            {/* Header */}
            <div className="admin-headerSearchFlex">
              <div className="admin-dashboard-header">
                <span className="admin-dashboard-header-content">
                  <strong>{orgData?.name}</strong>
                </span>
              </div>
              <div className="admin-dashboard-search" style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                {headerActions}
                <FilterPanel fields={filterFields} filters={filters} onFiltersChange={setFilters} height={31} />
              </div>
            </div>
            {tabsSlot ?? (
              <Typography variant="subtitle1" style={{ marginBottom: "1rem" }}>
                Pending Approvals
              </Typography>
            )}
            <FilterChips fields={filterFields} filters={filters} onFiltersChange={setFilters} />
            <Divider />
            {/* DataGrid */}
            <AdminDataGrid
              rows={rows}
              columns={columns}
              selectedRows={selectedRows}
              setSelectedRows={setSelectedRows}
              queryData={rows}
              setShowModalMulti={setShowModalMulti}
              setMultiSelectAction={setMultiSelectAction}
              isMultiActionLoading={isUpdateUserMembershipBulkLoading || isUpdateUserOrgStatusLoading || isMultiActionLoading}
              isTableLoading={(isUpdateUserMembershipBulkLoading || isUpdateUserOrgStatusLoading || isMultiActionLoading) || isFetchingOrgRequestsLoading}
              isInitialLoading={isOrgRequestsLoading && !rows.length}
              setCurrentPage={setCurrentPage}
              currentPage={currentPage}
              totalCount={orgRequestsData?.total || 0}
              recordsPerPage={recordsPerPage}
              filtersSlot={<FilterPanel fields={filterFields} filters={filters} onFiltersChange={setFilters} height={31} />}
              chipsSlot={<FilterChips fields={filterFields} filters={filters} onFiltersChange={setFilters} />}
              onRecordsPerPageChange={(size) => {
                setRecordsPerPage(size);
                setCurrentPage(1);
              }}
              canReviewRequests={canUpdateUser}
            />
            {/* Footer/Support Request */}
            <div
              className="admin-dashboard-support-container"
              style={{
                marginTop: "30px",
                padding: "10px 0px",
                paddingBottom: "2rem",
              }}
            >
              <p>
                Please fill our{" "}
                <a
                  href="https://deloitteglobal.service-now.com/sp?id=sc_cat_item&sys_id=4a417b4f1b17855cbcae55b0604bcb1c"
                  target="_blank"
                  className="request-support-link"
                  rel="noreferrer"
                >
                  Support Request
                </a>
                &nbsp; Form if you face any issues.
              </p>
            </div>
          </Box>
        </div>
      )}
    </Container>
  );
};

export default AdminDashboard;
